import { defineNuxtPlugin } from '#app';
import { useUserStore } from '~/store/user/userStore';
import { watch, useRouter } from '#imports';
import { type ExportedGlobalComposer } from 'vue-i18n';
import { isChannelTalkLeftRoute } from '~/utils/channelTalkPlacement';
import { isStorybookEnvironment } from '~~/package/utils/StorybookUtil';
import { getPolicyCookies } from '~~/package/utils/CookieUtil';
import { canBootChannelTalk } from '~/utils/cookieConsent';
import { useUtilsStore } from '~/store/utils/utilsStore';

const PLUGIN_KEY = '15784de7-2def-4fe2-8094-faf300c82fb0';
const SUPPORTED_LANGUAGES = ['ko', 'en', 'ja'] as const;
type Language = (typeof SUPPORTED_LANGUAGES)[number];
const STORAGE_PREFIXES = ['ch-', 'channel.io', '__ch_'];

const toLanguage = (locale: string): Language => {
    const lower = (locale || 'en').toLowerCase();
    return (SUPPORTED_LANGUAGES as readonly string[]).includes(lower)
        ? (lower as Language)
        : 'en';
};

// 공식 로더 스니펫 — window.ChannelIO 를 큐잉 stub 으로 등록한 뒤 ch-plugin-web.js 를 비동기 주입.
// SDK 도착 전 boot() 등을 호출해도 stub 이 큐에 쌓아 두므로 호출 순서를 신경 쓸 필요 없음.
//
// stub 등록(registerStub)은 plugin init 에서 *동기* 실행한다. boot()/SDK 주입은 로그인 시점에만
// 일어나므로 게스트에겐 런처가 노출되지 않는다(비로그인 비노출 정책). 스텁을 동기 등록해 두면
// 로그인 순간 호출되는 boot() 가 SDK 도착 전이어도 큐에 안전히 쌓여, 호출 순서에 무관하게
// SDK 로드 후 실행된다.
const registerStub = () => {
    if (window.ChannelIO) return;

    type ChStub = ((cmd: string, ...args: unknown[]) => void) & {
        c: (args: unknown[]) => void;
        q: unknown[];
    };
    const ch = function (this: unknown, ...args: unknown[]) {
        ch.c(args);
    } as ChStub;
    ch.q = [];
    ch.c = (args) => {
        ch.q.push(args);
    };
    window.ChannelIO = ch;
};

const injectSdk = () => {
    if (window.ChannelIOInitialized) return;
    window.ChannelIOInitialized = true;
    document.head.appendChild(
        Object.assign(document.createElement('script'), {
            type: 'text/javascript',
            async: true,
            src: 'https://cdn.channel.io/plugin/ch-plugin-web.js',
        })
    );
};

const scheduleInject = () => {
    if (document.readyState === 'complete') injectSdk();
    else {
        window.addEventListener('DOMContentLoaded', injectSdk);
        window.addEventListener('load', injectSdk);
    }
};

// 로그아웃 시 ch-* 쿠키/스토리지 직접 제거.
// shutdown() + 익명 boot() 만으로는 ch-session/ch-veil-id 가 남아, 공유 PC 다음 사용자가
// 직전 visitor 컨텍스트로 잡혀 이전 사용자의 대화 이력을 볼 수 있음. 공식 도움말 권장.
const clearChannelTalkData = () => {
    const matches = (name: string) =>
        STORAGE_PREFIXES.some((p) => name.startsWith(p));

    // 멀티 서브도메인 환경(dev.codepresso.io 등) 부모 도메인에도 같은 쿠키가 있을 수 있어 함께 만료.
    const host = window.location.hostname;
    const parts = host.split('.');
    const domains = new Set<string>(['', host, `.${host}`]);
    if (parts.length > 2) domains.add('.' + parts.slice(-2).join('.'));

    const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie
        .split(';')
        .map((c) => {
            const i = c.indexOf('=');
            return (i > -1 ? c.slice(0, i) : c).trim();
        })
        .filter(matches)
        .forEach((name) => {
            domains.forEach((d) => {
                document.cookie = d
                    ? `${name}=; ${expired}; path=/; domain=${d}`
                    : `${name}=; ${expired}; path=/`;
            });
        });

    for (const store of [localStorage, sessionStorage]) {
        try {
            Object.keys(store)
                .filter(matches)
                .forEach((k) => store.removeItem(k));
        } catch {
            // 사파리 시크릿 모드 등에서 storage 접근이 막히면 무시.
        }
    }
};

export default defineNuxtPlugin((nuxtApp) => {
    if (!PLUGIN_KEY) return;
    if (isStorybookEnvironment()) return;

    // 스텁을 *동기* 등록해 로그인 시점의 boot()/watcher 호출이 CDN 도착 전에도 window.ChannelIO
    // 큐에 쌓이도록 보장한다. boot()/SDK 주입(ensureSdk)은 로그인 상태에서만 트리거하므로
    // 게스트에겐 런처가 노출되지 않는다.
    registerStub();

    // SDK 주입은 로그인 사용자에게만, 그리고 즉시 한다(게스트에겐 CDN 스크립트 자체를 주입하지
    // 않음). 비로그인은 ensureSdk 자체가 호출되지 않으므로 익명 Lighthouse/CWV 측정 창에는
    // SDK 가 절대 진입하지 않는다. (운영 환경 early-return은 bafd88a56에서 제거되어 live에도 노출됨.
    // 노출 조건은 로그인 + Functional 쿠키 동의 — canBootChannelTalk 참고.)
    // (과거 마케팅 경로 스크롤 게이트는 이 login-only 정책에서 무의미 → 제거. 로그인 사용자가
    //  / 에서 스크롤을 해야만 런처가 뜨던 문제를 해결.) scheduleInject 는 document 가 이미
    // 로드됐으면 즉시, 아니면 DOMContentLoaded/load 에 주입한다. 중복 스케줄 방지.
    let sdkScheduled = false;
    const ensureSdk = () => {
        if (sdkScheduled) return;
        sdkScheduled = true;
        scheduleInject();
    };

    const userStore = useUserStore();
    const i18n = nuxtApp.$i18n as unknown as ExportedGlobalComposer & {
        locale: { value: string };
    };
    const ch = (cmd: string, ...args: unknown[]) =>
        window.ChannelIO?.(cmd, ...args);

    const buildBoot = () => {
        const user = userStore.userV2;
        const base = {
            pluginKey: PLUGIN_KEY,
            language: toLanguage(i18n.locale.value),
        };
        if (!user?.uuid) return base;
        return {
            ...base,
            memberId: user.uuid,
            profile: {
                name: user.fullName || undefined,
                email: user.email || undefined,
                avatarUrl: user.profileImageUrl || undefined,
                locale: user.locale || undefined,
            },
        };
    };

    // 새로고침 등으로 이미 로그인된 경우에만 초기 부팅. 게스트는 SDK 주입·boot 모두 생략.
    // Functional 쿠키 동의(2026-07 쿠키 정책 v1.1)가 있어야 boot — 미동의/미확정이면 노출하지 않는다.
    if (canBootChannelTalk(userStore.userV2.uuid, getPolicyCookies().functional)) {
        ensureSdk();
        ch('boot', buildBoot());
    }

    // ── 네이티브 런처 버튼 위치 + TOP 버튼 스택 ───────────────────────────────
    // 런처 버튼은 #ch-plugin-entry 하위 open shadow root 안 [data-ch-testid="launcher"] 이다.
    // 외부(document) CSS 는 shadow 경계를 못 넘으므로(=#ch-plugin-entry>div 에 CSS 넣어도 무효),
    // open shadow root 안에 <style> 을 직접 주입한다. 좌측 라우트에서 런처/팝업을 좌측으로
    // 옮기는 가로 배치에만 쓴다(세로 bottom 은 채널톡 기본값 유지 → TOP 버튼이 채널톡 위로).
    // 네이티브 버튼은 열림/닫힘을 한 요소가 토글하므로 이 요소를 옮기면 닫기(X) 버튼도 함께 이동.
    const router = useRouter();
    const currentRouteName = () => router.currentRoute.value.name;

    const LAUNCHER_STYLE_ID = 'gp-ch-launcher-pos';
    const LAUNCHER_MARGIN = 20; // px, 좌측 마진
    const TOP_GAP = 12; // px, 런처와 TOP 버튼 사이 간격(TOP 버튼을 런처 위로 올릴 때)
    const TOP_BUTTON_SELECTORS = ['#topBtn', '.topBtn'];

    // 마케팅 팝업 버블("궁금한 건 채팅으로 문의하세요")은 런처와 같은 shadow root 안의 별도
    // fixed 요소다. 좌측 라우트에서 런처와 같은 쪽(좌측)을 유지하도록 가로 배치만 함께 적용한다.
    // styled-components 클래스의 해시(__sc-…, bgObeb)는 빌드마다 바뀌므로 안정적인
    // 컴포넌트 표시명 프리픽스로 매칭한다(런처가 [data-ch-testid="launcher"] 를 쓰는 것과 동일 취지).
    const POPUP_SELECTOR = '[class*="PCBubblePopupLayer"]';

    type ShadowHost = HTMLElement & { shadowRoot: ShadowRoot };
    const getShadowHost = (): ShadowHost | null => {
        const entry = document.querySelector('#ch-plugin-entry');
        if (!entry) return null;
        const cands = [entry, ...entry.querySelectorAll('*')] as HTMLElement[];
        return (cands.find((el) => el.shadowRoot) as ShadowHost) ?? null;
    };

    const isElVisible = (el: HTMLElement): boolean => {
        const cs = getComputedStyle(el);
        // opacity 는 무시 — #topBtn 은 0→1 페이드라 opacity 체크 시 초기에 놓침.
        return (
            cs.display !== 'none' &&
            cs.visibility !== 'hidden' &&
            el.getClientRects().length > 0
        );
    };

    // 현재 보이는 채널톡 런처의 위치 rect. 없거나 숨김이면 null.
    const launcherRect = (): DOMRect | null => {
        const host = getShadowHost();
        const el = host?.shadowRoot.querySelector(
            '[data-ch-testid="launcher"]'
        ) as HTMLElement | null;
        return el && isElVisible(el) ? el.getBoundingClientRect() : null;
    };

    // 좌측 라우트에서만 런처·팝업을 좌측으로(가로 배치). 세로 bottom 은 손대지 않아
    // 채널톡 기본값을 그대로 쓴다(TOP 버튼이 채널톡 위로 올라가는 정책).
    const buildLauncherCss = (): string => {
        if (!isChannelTalkLeftRoute(currentRouteName())) return '';
        const horiz = `left:${LAUNCHER_MARGIN}px !important;right:auto !important;`;
        return `[data-ch-testid="launcher"]{${horiz}}${POPUP_SELECTOR}{${horiz}}`;
    };

    // TOP 버튼을 채널톡 런처 *위*로 올린다(채널톡과 같은 쪽일 때만). 런처가 보이지 않으면
    // 인라인 bottom 을 제거해 CSS 기본값(bottom:20px)으로 되돌린다. 채널톡 런처 자체는
    // 기본 위치에 그대로 두고 충돌하는 TOP 버튼만 끌어올린다(= 기존 회피 로직의 반대).
    const positionTopButton = () => {
        const rect = launcherRect();
        const launcherLeft =
            rect != null && rect.left + rect.width / 2 < window.innerWidth / 2;
        // 뷰포트 하단 ~ 런처 상단 + 간격 = TOP 버튼을 런처 바로 위에 놓을 bottom(px).
        const raised =
            rect != null ? Math.round(window.innerHeight - rect.top + TOP_GAP) : null;

        for (const sel of TOP_BUTTON_SELECTORS) {
            document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
                const r = el.getBoundingClientRect();
                const topLeft = r.left + r.width / 2 < window.innerWidth / 2;
                if (raised != null && topLeft === launcherLeft) {
                    const target = `${raised}px`;
                    if (el.style.getPropertyValue('bottom') !== target) {
                        el.style.setProperty('bottom', target, 'important');
                    }
                } else if (el.style.getPropertyValue('bottom')) {
                    el.style.removeProperty('bottom'); // 기본 CSS bottom 복원
                }
            });
        }
    };

    // host 발견 시: shadow root 에 <style> 확보 후 현재 상태로 textContent 재작성. 성공하면 true.
    const applyLauncher = (): boolean => {
        const host = getShadowHost();
        if (!host) return false;
        let style = host.shadowRoot.getElementById(
            LAUNCHER_STYLE_ID
        ) as HTMLStyleElement | null;
        if (!style) {
            style = document.createElement('style');
            style.id = LAUNCHER_STYLE_ID;
            host.shadowRoot.appendChild(style);
        }
        const css = buildLauncherCss();
        if (style.textContent !== css) style.textContent = css; // 변경 시에만(불필요 갱신/루프 방지)
        return true;
    };

    // ── 메신저 창 위치: 특정 페이지에서 좌측 하단 ─────────────────────────────
    // 데스크톱 메신저 창은 light DOM 의 #ch-plugin-script (iframe 컨테이너)이고, 인라인
    // style="right:20px !important; bottom:86px !important" 로 위치가 박혀 있다. 인라인 !important
    // 는 스타일시트로 못 덮으므로, 같은 요소의 인라인 left/right 를 JS 로 직접 덮어쓴다. 채널톡이
    // 열 때마다 right 를 재설정하므로 style 변경을 관찰해 root 에서 left 를 유지한다.
    // 세로(bottom)는 채널톡 기본값을 그대로 둔다(TOP 회피는 TOP 버튼 쪽에서 처리).
    // (창 내부는 cross-origin iframe 이라 건드리지 않고 외곽 컨테이너만 이동.)
    const MESSENGER_ID = 'ch-plugin-script';
    const MESSENGER_MARGIN = '20px';
    let watchedMessenger: HTMLElement | null = null;
    let messengerStyleObserver: MutationObserver | null = null;

    const positionMessenger = () => {
        const el = document.getElementById(MESSENGER_ID);
        if (!el) return;
        // 데스크톱 창만 대상 — 모바일은 전체화면이라 left/right 가 무의미.
        const isDesktop = el.classList.contains('ch-desk-messenger');
        const left = isChannelTalkLeftRoute(currentRouteName());
        const onLeftDesktop = isDesktop && left;

        if (onLeftDesktop) {
            // 이미 좌측이면 재설정 안 함(관찰 콜백 루프 방지).
            if (el.style.getPropertyValue('left') !== MESSENGER_MARGIN) {
                el.style.setProperty('left', MESSENGER_MARGIN, 'important');
                el.style.setProperty('right', 'auto', 'important');
            }
        } else if (el.style.getPropertyValue('left')) {
            // 우측 복귀: 우리가 넣은 left 제거 + right 복원(열린 채 이동해도 어긋나지 않게).
            el.style.removeProperty('left');
            el.style.setProperty('right', MESSENGER_MARGIN, 'important');
        }

        // 동일 요소에 style/class 변경 관찰 1회 부착(채널톡이 열 때마다 right 재설정 → left 유지).
        if (watchedMessenger !== el) {
            messengerStyleObserver?.disconnect();
            watchedMessenger = el;
            messengerStyleObserver = new MutationObserver(() => positionMessenger());
            messengerStyleObserver.observe(el, {
                attributes: true,
                attributeFilter: ['style', 'class'],
            });
        }
    };

    // 런처/창(좌/우 가로 배치)과 TOP 버튼(채널톡 위로)을 함께 재배치. rAF 로 버스트 코얼레싱.
    let scheduled = false;
    const reposition = () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
            scheduled = false;
            applyLauncher();
            positionMessenger();
            positionTopButton();
        });
    };

    // DOM 변화 감지 → 재배치:
    //  - #topBtn(v-if add/remove) / .topBtn(mount/unmount) → TOP 버튼 노출 변화
    //  - #ch-plugin / #ch-plugin-script 등장 → 런처 shadow host / 메신저 창 생성
    new MutationObserver(reposition).observe(document.body, {
        childList: true,
        subtree: true,
    });
    // 뷰포트 크기 변화 시 raised bottom 재계산.
    window.addEventListener('resize', reposition, { passive: true });

    // 초기 진입
    applyLauncher();
    positionMessenger();
    positionTopButton();
    // SPA 네비게이션
    router.afterEach(() => {
        applyLauncher();
        positionMessenger();
        positionTopButton();
    });

    // 로그아웃(회원→게스트): shutdown + ch-* 클리어(다음 사용자 노출 방지). 재부팅 안 함 → 게스트 비노출.
    // 로그인(게스트→회원, 계정 전환): SDK 로드 후 회원 컨텍스트로 boot → 이때만 런처 노출.
    watch(
        () => userStore.userV2.uuid,
        (newId, oldId) => {
            if (newId === oldId) return;
            ch('shutdown');
            if (oldId && !newId) clearChannelTalkData();
            if (canBootChannelTalk(newId, getPolicyCookies().functional)) {
                ensureSdk();
                ch('boot', buildBoot());
                reposition(); // 재부팅 시 shadow host/창 재생성 → 버튼·창 재배치
            }
        }
    );

    // 쿠키 배너에서 Functional 동의가 저장/철회되면 즉시 반영한다.
    // (쿠키 자체는 반응형이 아니므로 utilsStore.functionalConsent를 신호로 사용)
    const utilsStore = useUtilsStore();
    watch(
        () => utilsStore.functionalConsent,
        (consent) => {
            if (consent === true) {
                if (userStore.userV2.uuid) {
                    ensureSdk();
                    ch('boot', buildBoot());
                    reposition();
                }
            } else if (consent === false) {
                ch('shutdown');
                clearChannelTalkData();
            }
        }
    );

    // 프로필 필드 변경 시 채널톡 사용자 정보만 갱신 (세션 유지).
    watch(
        () => [
            userStore.userV2.fullName,
            userStore.userV2.email,
            userStore.userV2.locale,
        ],
        () => {
            if (!userStore.userV2.uuid) return;
            ch('updateUser', {
                profile: {
                    name: userStore.userV2.fullName || undefined,
                    email: userStore.userV2.email || undefined,
                    locale: userStore.userV2.locale || undefined,
                },
            });
        }
    );

    // UI 언어 변경 시 메신저 언어 동기화. updateUser({ language }) 는 top-level language 를 지원해
    // 세션(대화 이력)을 유지한 채 메신저 UI 언어만 갱신 — shutdown/boot 불필요.
    // es/vi 처럼 'en' 으로 폴백되는 locale 간 전환은 동일 언어로 매핑돼 호출 스킵.
    watch(i18n.locale, (newRaw, oldRaw) => {
        const next = toLanguage(newRaw || 'en');
        if (next === toLanguage(oldRaw || 'en')) return;
        ch('updateUser', { language: next });
    });
});
